/**
 * The kid's chore screen. Big targets, their own colour, and an honest split
 * between money that is BANKED and money that is still WAITING on a grown-up.
 * A kid only ever gets their own data here — the Worker refuses anything else.
 */
import { useState } from 'react';
import { api, ApiRequestError, money } from '../../lib/api';
import type { Chore } from '../../shared/types';
import type { KidData } from './ChoresPage';
import {
  Avatar,
  Banner,
  CADENCE_LABEL,
  Card,
  EmptyState,
  SectionTitle,
  btnPrimary,
  choreState,
  cx,
  shortDate,
} from './shared';

export default function KidView({ data, reload }: { data: KidData; reload: () => Promise<void> }) {
  const { chores, current, thisWeek, me, balance, history } = data;
  const [busyId, setBusyId] = useState<number | null>(null);
  const [flash, setFlash] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  const color = me?.color ?? 'var(--color-sage)';
  const memberId = me?.id ?? null;

  // `current` and `thisWeek` are both windowed by the Worker in the household's
  // timezone. Nothing here decides what day it is — that is exactly how the button
  // and the Worker's 409 used to end up disagreeing.
  const states = chores.map((chore) => ({ chore, ...choreState(chore, current, memberId) }));
  const doneNow = states.filter((s) => s.status !== 'todo').length;

  async function markDone(chore: Chore) {
    setBusyId(chore.id);
    setFlash(null);
    try {
      await api.post(`/chores/${chore.id}/complete`);
      await reload();
      setFlash({
        tone: 'ok',
        message:
          chore.rate > 0
            ? `Nice! ${money(chore.rate)} is waiting for a grown-up to say yes.`
            : 'Nice! A grown-up will check it off.',
      });
    } catch (err) {
      setFlash({
        tone: 'error',
        message:
          err instanceof ApiRequestError
            ? err.message
            : 'That did not save. Try again in a second.',
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Hero — their name, their colour. */}
      <div
        className="rounded-card border border-border p-5"
        style={{ backgroundColor: `color-mix(in srgb, ${color} 10%, var(--color-surface))` }}
      >
        <div className="flex items-center gap-4">
          <Avatar member={me} size="lg" />
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink">
              Hi, {me?.name ?? 'friend'}
            </h1>
            <p className="text-sm text-ink-soft">
              {thisWeek.length === 0
                ? 'Nothing checked off yet this week.'
                : `${thisWeek.length} chore${thisWeek.length === 1 ? '' : 's'} checked off this week.`}
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border bg-surface p-4">
            <p className="text-xs font-medium tracking-wide text-ink-soft uppercase">Banked</p>
            <p className="mt-1 font-display text-3xl font-semibold text-sage">
              {money(balance.balance)}
            </p>
            <p className="mt-1 text-xs text-ink-faint">Yours. Approved and counted.</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4">
            <p className="text-xs font-medium tracking-wide text-ink-soft uppercase">Waiting</p>
            <p className="mt-1 font-display text-3xl font-semibold text-honey">
              {money(balance.pending)}
            </p>
            <p className="mt-1 text-xs text-ink-faint">Needs a grown-up to say yes.</p>
          </div>
        </div>
      </div>

      {flash ? <Banner tone={flash.tone} message={flash.message} /> : null}

      {/* Chores */}
      <div>
        <SectionTitle
          action={
            chores.length > 0 ? (
              <span className="text-sm text-ink-soft">
                {doneNow} of {chores.length} done
              </span>
            ) : undefined
          }
        >
          Your chores
        </SectionTitle>

        {chores.length === 0 ? (
          <EmptyState
            title="No chores yet"
            body="When a grown-up adds a chore for you, it shows up right here — with what it pays."
          />
        ) : (
          <ul className="space-y-3">
            {states.map(({ chore, status, completion }) => (
              <li key={chore.id}>
                <Card
                  className={cx(
                    'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
                    status === 'todo' ? '' : 'bg-surface-sunk'
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h3 className="font-display text-lg font-semibold text-ink">{chore.name}</h3>
                      {chore.rate > 0 ? (
                        <span className="rounded-full bg-honey-soft px-2.5 py-0.5 text-sm font-semibold text-honey">
                          {money(chore.rate)}
                        </span>
                      ) : null}
                    </div>
                    {chore.description ? (
                      <p className="mt-1 text-sm text-ink-soft">{chore.description}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-ink-faint">
                      {CADENCE_LABEL[chore.cadence]}
                      {chore.assigned_to === null ? ' · Anyone can do this one' : ''}
                    </p>
                  </div>

                  {status === 'todo' ? (
                    <button
                      type="button"
                      onClick={() => void markDone(chore)}
                      disabled={busyId === chore.id}
                      className={cx(btnPrimary, 'min-h-[56px] w-full px-6 text-base sm:w-auto')}
                    >
                      {busyId === chore.id ? 'Saving…' : 'I did it!'}
                    </button>
                  ) : (
                    <p
                      className={cx(
                        'flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl px-4 text-sm font-medium',
                        status === 'approved'
                          ? 'bg-sage-soft text-sage'
                          : 'bg-honey-soft text-honey'
                      )}
                    >
                      <span aria-hidden="true">{status === 'approved' ? '✓' : '⏳'}</span>
                      {status === 'approved'
                        ? `Approved${completion ? ` · ${money(completion.rate_at_time)}` : ''}`
                        : 'Waiting for a grown-up'}
                    </p>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Money history */}
      <div>
        <SectionTitle>Your money</SectionTitle>
        {history.length === 0 ? (
          <EmptyState
            title="Nothing earned yet"
            body="Check off a chore above. Once a grown-up approves it, the money lands here."
          />
        ) : (
          <Card className="p-0">
            <ul className="divide-y divide-border">
              {history.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{entry.reason}</p>
                    <p className="text-xs text-ink-faint">{shortDate(entry.created_at)}</p>
                  </div>
                  <p
                    className={cx(
                      'shrink-0 text-sm font-semibold tabular-nums',
                      entry.delta >= 0 ? 'text-sage' : 'text-clay'
                    )}
                  >
                    {entry.delta >= 0 ? '+' : '−'}
                    {money(Math.abs(entry.delta))}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
